import { Box, InputBase, Toolbar, Typography, IconButton, Menu, MenuItem, Badge, Avatar } from "@mui/material";
import { useState } from "react";
import { Search, Settings, Help, Apps, CloudQueue } from "@mui/icons-material";
import { useTransferQueue } from "./app/transferQueue";

interface HeaderProps {
  search: string;
  onSearchChange: (search: string) => void;
  setShowProgressDialog: (show: boolean) => void;
}

export default function Header({
  search,
  onSearchChange,
  setShowProgressDialog,
}: HeaderProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const transferQueue = useTransferQueue();
  const activeTransfers = transferQueue.filter(task =>
    task.status === 'pending' || task.status === 'in-progress'
  ).length;

  return (
    <Toolbar
      disableGutters
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1,
        minHeight: { xs: 56, sm: 64 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 0 0 rgba(0,0,0,0.03)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
        <CloudQueue sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography
          variant="h6"
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontWeight: 400,
            fontSize: '1.375rem',
            color: 'text.secondary',
            letterSpacing: 0,
          }}
        >
          Flare Drive
        </Typography>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          maxWidth: { sm: 720 },
        }}
      >
        <Search
          sx={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'text.secondary',
            fontSize: 20,
          }}
        />
        <InputBase
          size="small"
          fullWidth
          placeholder="Search in Drive"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            backgroundColor: '#f1f3f4',
            borderRadius: '8px',
            padding: '8px 16px 8px 44px',
            fontSize: '0.875rem',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#e8eaed',
            },
            '&.Mui-focused': {
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
        <IconButton
          aria-label="Upload progress"
          size="medium"
          onClick={() => setShowProgressDialog(true)}
          sx={{
            color: activeTransfers > 0 ? 'primary.main' : 'text.secondary',
          }}
        >
          <Badge badgeContent={activeTransfers} color="error">
            <CloudQueue fontSize="small" />
          </Badge>
        </IconButton>

        <IconButton
          aria-label="Help"
          size="medium"
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary' }}
        >
          <Help fontSize="small" />
        </IconButton>

        <IconButton
          aria-label="Settings"
          size="medium"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: 'text.secondary' }}
        >
          <Settings fontSize="small" />
        </IconButton>

        <IconButton
          aria-label="Google apps"
          size="medium"
          sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary' }}
        >
          <Apps fontSize="small" />
        </IconButton>

        <Avatar
          sx={{
            width: 32,
            height: 32,
            ml: 1,
            bgcolor: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          U
        </Avatar>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => setAnchorEl(null)}>Display density</MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>Settings</MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setShowProgressDialog(true);
          }}
        >
          Upload Progress
        </MenuItem>
      </Menu>
    </Toolbar>
  );
}
